import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Alert, Space, Spin } from "antd";
import {
  LessonFormProvider,
  useLessonForm,
} from "../../../components/Forms/LessonForm/components/LessonFormContext";
import ActivityCard from "../../../components/Forms/LessonForm/components/ActivityCard";
import { getActivitiesByType } from "../../../components/Forms/LessonForm/utils";
import type { OdReviewIssue } from "../../../services/odReview/odReview.types";
import {
  applyPatchClientSide,
  readCurrentValue,
  resolveIndices,
  writeValue,
} from "./utils";
import { isPhotoIssue } from "./IssueCard.utils";
import JudgedPhoto from "./JudgedPhoto";

export interface ActivityUnderReviewHandle {
  getAppliedValue: () => string | null;
  confirmAccepted: (issue: OdReviewIssue, appliedValue: string) => void;
}

type PristineCache = Map<string, string | null>;

const pristineFieldKey = (issue: OdReviewIssue): string =>
  `${issue.activity_id}:${issue.block_id ?? "null"}:${issue.target_field}`;

interface PatchInjectorProps {
  issue: OdReviewIssue;
  activityIndex: number;
  blockIndex: number;
  pristineValuesAcrossLessonsRef: React.MutableRefObject<PristineCache>;
  children: React.ReactNode;
}

const PatchInjector: React.FC<PatchInjectorProps> = ({
  issue,
  activityIndex,
  blockIndex,
  pristineValuesAcrossLessonsRef,
  children,
}) => {
  const {
    lesson,
    handleActivityUpdate,
    handleBlockTextChange,
    handleBlockMaterialsChange,
  } = useLessonForm();

  useEffect(() => {
    if (activityIndex < 0) return;
    if (issue.operation === "manual" || issue.suggested_value === null) return;

    const setters = {
      handleActivityUpdate,
      handleBlockTextChange,
      handleBlockMaterialsChange,
    };

    const key = pristineFieldKey(issue);
    let pristine = pristineValuesAcrossLessonsRef.current.get(key);
    if (pristine === undefined) {
      pristine = readCurrentValue(lesson, issue, activityIndex, blockIndex);
      pristineValuesAcrossLessonsRef.current.set(key, pristine);
    }

    const patched = applyPatchClientSide(pristine, issue);
    if (patched === null) return;
    writeValue(setters, issue, activityIndex, blockIndex, patched);

    const revertToBaselineAsOfCleanupTime = () => {
      const baselineAtCleanupTime =
        pristineValuesAcrossLessonsRef.current.get(key);
      if (baselineAtCleanupTime !== null && baselineAtCleanupTime !== undefined) {
        writeValue(setters, issue, activityIndex, blockIndex, baselineAtCleanupTime);
      }
    };

    return revertToBaselineAsOfCleanupTime;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.id, activityIndex, blockIndex]);

  return <>{children}</>;
};

interface ActivityUnderReviewContentProps {
  issue: OdReviewIssue;
  pristineValuesAcrossLessonsRef: React.MutableRefObject<PristineCache>;
  handleRef: React.Ref<ActivityUnderReviewHandle>;
}

const ActivityUnderReviewContent: React.FC<ActivityUnderReviewContentProps> = ({
  issue,
  pristineValuesAcrossLessonsRef,
  handleRef,
}) => {
  const { lesson, isLoading, fileUrls } = useLessonForm();

  useImperativeHandle(
    handleRef,
    () => ({
      getAppliedValue: () => {
        if (!lesson) return null;
        const { activityIndex, blockIndex } = resolveIndices(lesson, issue);
        return readCurrentValue(lesson, issue, activityIndex, blockIndex);
      },
      confirmAccepted: (acceptedIssue, appliedValue) => {
        pristineValuesAcrossLessonsRef.current.set(
          pristineFieldKey(acceptedIssue),
          appliedValue,
        );
      },
    }),
    [lesson, issue, pristineValuesAcrossLessonsRef],
  );

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: 48 }}
      >
        <Spin size="large" tip="Cargando actividad..." />
      </div>
    );
  }

  const { activityIndex, blockIndex } = resolveIndices(lesson, issue);
  const activityMissing = activityIndex < 0;
  const blockMissing = issue.block_id !== null && blockIndex < 0;

  if (activityMissing || blockMissing) {
    return (
      <Alert
        type="warning"
        showIcon
        message="La actividad cambió; revisá manualmente."
      />
    );
  }

  const activity = lesson.activities[activityIndex];
  const typeIndex = getActivitiesByType(lesson, activity.activityType).findIndex(
    (a) => a.id === activity.id,
  );

  return (
    <PatchInjector
      issue={issue}
      activityIndex={activityIndex}
      blockIndex={blockIndex}
      pristineValuesAcrossLessonsRef={pristineValuesAcrossLessonsRef}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {isPhotoIssue(issue) && (
          <JudgedPhoto
            fileId={issue.photo_file_id}
            photoUrl={
              issue.photo_file_id ? fileUrls[issue.photo_file_id] : undefined
            }
          />
        )}
        <ActivityCard
          activity={activity}
          typeIndex={typeIndex}
          activityType={activity.activityType}
        />
      </Space>
    </PatchInjector>
  );
};

interface ActivityUnderReviewProps {
  issue: OdReviewIssue;
}

const ActivityUnderReview = forwardRef<
  ActivityUnderReviewHandle,
  ActivityUnderReviewProps
>(({ issue }, ref) => {
  const pristineValuesAcrossLessonsRef = useRef<PristineCache>(new Map());

  return (
    <LessonFormProvider key={issue.lesson_id} lessonId={String(issue.lesson_id)}>
      <ActivityUnderReviewContent
        issue={issue}
        pristineValuesAcrossLessonsRef={pristineValuesAcrossLessonsRef}
        handleRef={ref}
      />
    </LessonFormProvider>
  );
});

ActivityUnderReview.displayName = "ActivityUnderReview";

export default ActivityUnderReview;
